import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import SearchableMultiSelect from '@/temu-platform/modules/products/components/SearchableMultiSelect';

/**
 * 产品信息「复制到草稿箱」：选择目标上架站点（可搜索多选下拉）。
 */
export default function TemuCopyProductSitesModal({
  open,
  title = '',
  sites = [],
  busy = false,
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation('workbench');
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState('');

  const options = useMemo(
    () => (Array.isArray(sites) ? sites : []).filter((row) => row?.value != null && String(row.value).trim() !== ''),
    [sites],
  );

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setError('');
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const submit = async (event) => {
    event?.preventDefault?.();
    if (busy) return;
    const sitesSelected = (Array.isArray(selected) ? selected : [])
      .map((v) => String(v || '').trim())
      .filter(Boolean);
    if (!sitesSelected.length) {
      setError(t('productCopy.needSite'));
      return;
    }
    await onConfirm?.(sitesSelected);
  };

  return createPortal(
    <div className="tpo-batch-modal" role="presentation">
      <button
        type="button"
        className="tpo-batch-modal__backdrop"
        aria-label={t('productCopy.close')}
        onClick={() => { if (!busy) onClose?.(); }}
      />
      <div
        className="tpo-batch-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="temu-copy-product-sites-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tpo-batch-modal__head">
          <h2 id="temu-copy-product-sites-title">{t('productCopy.title')}</h2>
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={onClose} disabled={busy}>
            {t('productCopy.close')}
          </button>
        </div>
        <form className="tpo-batch-modal__body" onSubmit={submit}>
          <p className="tpo-batch-modal__meta" style={{ marginTop: 0 }}>
            {title
              ? t('productCopy.withTitle', { title })
              : t('productCopy.withoutTitle')}
          </p>
          <div className="tpo-field tpo-field--full">
            <span className="tpo-field__label">{t('productCopy.targetSites')}</span>
            {options.length === 0 ? (
              <p className="tpo-batch-modal__hint">{t('productCopy.noSites')}</p>
            ) : (
              <SearchableMultiSelect
                value={selected}
                onChange={(next) => {
                  setSelected(Array.isArray(next) ? next.map(String) : []);
                  setError('');
                }}
                options={options}
                placeholder={t('productCopy.placeholder')}
                disabled={busy}
                emptyText={t('productCopy.empty')}
              />
            )}
            {error ? <span className="tpo-field__error">{error}</span> : null}
          </div>
        </form>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--secondary" onClick={onClose} disabled={busy}>
            {t('shop.cancel')}
          </button>
          <button type="submit" className="tpo-btn tpo-btn--primary" disabled={busy || options.length === 0} onClick={submit}>
            {busy ? t('productTable.copying') : t('productCopy.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
